import React, { useState } from "react";
import { Button, Flex, Modal, Space, Row, Col, Spin, Tag } from "antd";
import { Link, useSearchParams } from "react-router-dom";
import {
  mdiDotsHorizontalCircleOutline,
  mdiFileDownload,
  mdiInformationOutline,
  mdiMapMarkerOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Document, Page } from "react-pdf";
import Legends from "../Legends";

const ListingsV1 = ({ itineraries = [], itineraryIdentifier = "id" }) => {
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [itineraryModalOpen, setItineraryModalOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [descriptionData, setDescriptionData] = useState("");
  const [fileUrlData, setFileUrlData] = useState("");
  const [itineraryData, setItineraryData] = useState(null);
  const [departureDatesData, setDepartureDatesData] = useState([]);
  const [selectedDepartureDate, setSelectedDepartureDate] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(1000);
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  const showModal = (modalSetter, propSetter, value) => {
    modalSetter(true);
    propSetter(value);
  };

  const closeModal = (modalSetter) => {
    modalSetter(false);
  };

  const openDepartureDatesModal = (itinerary, departureDates) => {
    setDateModalOpen(true);
    setItineraryData(itinerary);
    setDepartureDatesData(departureDates);
  };

  const getItineraryUrl = (itinerary, departureDate = itinerary?.departureDate?.[0]) => {
    if (!itinerary) return "";

    const query = new URLSearchParams();
    if (type) query.set("type", type);
    if (departureDate) query.set("depart_date", departureDate);

    const identifier = itineraryIdentifier && itineraryIdentifier == "code" ? itinerary.code : itinerary.id;
    const queryString = query.toString();
    return `/web/itinerary/${identifier}${queryString ? `?${queryString}` : ""}`;
  };

  const itineraryLinkByDate = (itinerary, date) =>
    getItineraryUrl(itinerary, date);

  const onDocumentLoadSuccess = async (pdfDoc) => {
    const page = await pdfDoc.getPage(1);
    setNumPages(pdfDoc["_pdfInfo"]["numPages"]);
    setPageWidth(page.view[2] + 50);
  };

  const departureDate = (type, date) => (
    <span
      className={`departure-date ${type} ${selectedDepartureDate === date && "selected"}`}
      onClick={() => setSelectedDepartureDate(date)}
    >
      {date}
    </span>
  );

  const getCategoryImage = (category: string): string => {
    if (!category) return "";
    const categoryMap: Record<string, string> = {
      "GD Standard": "/iceholidays-assets/images/icons/gd.png",
      FS: "/iceholidays-assets/images/icons/fs.png",
      "GD Premium": "/iceholidays-assets/images/icons/premium.png",
      "GD Domestic": "/iceholidays-assets/images/icons/gd_domestic.png",
      "GD Signature": "/iceholidays-assets/images/icons/gd_signature.png",
      "GD FLY CRUISE": "/iceholidays-assets/images/icons/fly_cruise.png",
    };
    if (categoryMap[category]) return categoryMap[category];
    if (category.includes("CRUISE"))
      return "/iceholidays-assets/images/icons/cruise.png";
    if (category.includes("GD In Depth"))
      return "/iceholidays-assets/images/icons/gd.png";
    return "";
  };

  return (
    <div className="listings">
      <Legends />

      <div id="tours">
        <Flex vertical gap={30}>
          {itineraries.map((itinerary) => {
            const departureDates = itinerary.departureDate
              ? itinerary.departureDate.map((ddate) => {
                  const type = itinerary.guranteedDepartureDates?.includes(
                    ddate,
                  )
                    ? "guaranteed"
                    : itinerary.almostGuaranteedDepartureDates?.includes(ddate)
                      ? "almost-guaranteed"
                      : "";

                  return { type, date: ddate };
                })
              : [];

            const categoryImage = getCategoryImage(itinerary.category);

            return (
              <Row key={itinerary.id} className="tour">
                <Col flex="0 1 25%" className="column">
                  <div className="tour_image_overlay_wrapper">
                    <div
                      className="tour_image"
                      style={{ backgroundImage: `url(${itinerary.images[0]})` }}
                    ></div>
                    {itinerary.isNew && (
                      <span className="new_badge new_badge--overlay">New</span>
                    )}
                  </div>
                </Col>
                <Col flex="1" className="column">
                  <div className="tour_details">
                    <Space
                      size={20}
                      direction="vertical"
                      style={{ display: "flex" }}
                    >
                      <Space size={10} direction="vertical">
                        <div className="tour_details_title">
                          {itinerary.caption}
                        </div>
                        <div className="tour_details_info">
                          <span
                            onClick={() =>
                              showModal(
                                setDescriptionModalOpen,
                                setDescriptionData,
                                itinerary.description,
                              )
                            }
                          >
                            <Icon path={mdiInformationOutline} size={1} />
                          </span>
                        </div>
                        <div>
                          <span className="tour_details_country">
                            <Icon path={mdiMapMarkerOutline} size="18px" />{" "}
                            {itinerary.country}
                          </span>
                          <span
                            className="tour_details_code"
                            onClick={() =>
                              showModal(
                                setItineraryModalOpen,
                                setFileUrlData,
                                itinerary.fileUrlCopy,
                              )
                            }
                          >
                            <Icon path={mdiFileDownload} size="18px" />{" "}
                            {itinerary.code}
                          </span>
                          {itinerary.departureLocations &&
                            itinerary.departureLocations.length > 0 && (
                              <span className="tour_details_locations">
                                {itinerary.departureLocations.map(
                                  (location, index) => (
                                    <Tag
                                      key={`${location}-${index}`}
                                      color="red"
                                    >
                                      {location}
                                    </Tag>
                                  ),
                                )}
                              </span>
                            )}
                          {categoryImage && (
                            <span className="tour_details_category">
                              <img
                                src={categoryImage}
                                alt={itinerary.category}
                              />
                            </span>
                          )}
                        </div>
                      </Space>
                      <div className="tour_details_dates">
                        <Space
                          size={6}
                          direction="vertical"
                          style={{ display: "flex" }}
                        >
                          <label>Departure Date(s)</label>
                          <div className="date-selector">
                            {departureDates &&
                              departureDates.slice(0, 9).map((ddate, index) => (
                                <Link
                                  key={index}
                                  to={itineraryLinkByDate(
                                    itinerary,
                                    ddate.date,
                                  )}
                                >
                                  {departureDate(ddate.type, ddate.date)}
                                </Link>
                              ))}
                            {departureDates.length >= 9 && (
                              <div
                                className="show-all-dates"
                                onClick={() =>
                                  openDepartureDatesModal(
                                    itinerary,
                                    departureDates,
                                  )
                                }
                              >
                                <Icon
                                  path={mdiDotsHorizontalCircleOutline}
                                  size="15px"
                                />{" "}
                                Show All
                              </div>
                            )}
                          </div>
                        </Space>
                      </div>
                    </Space>
                  </div>
                </Col>
                <Col className="column">
                  <div className="tour_pricing">
                    <Space size={20} direction="vertical" align="center">
                      <Flex className="tour_pricing_details">
                        <span>From</span>
                        <span className="price">{itinerary.price}</span>
                        <span>All In</span>
                      </Flex>
                      <Link
                        className="select-tour"
                        to={getItineraryUrl(itinerary)}
                      >
                        Select
                      </Link>
                    </Space>
                  </div>
                </Col>
              </Row>
            );
          })}
        </Flex>
      </div>

      <Modal
        title="Description"
        open={descriptionModalOpen}
        onCancel={() => closeModal(setDescriptionModalOpen)}
        footer={null}
        width={1000}
        centered
        className="tour_details_description"
      >
        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {descriptionData}
        </Markdown>
      </Modal>

      <Modal
        title="Itinerary"
        width="auto"
        maskClosable={false}
        centered
        className="tour_details_itinerary"
        open={itineraryModalOpen}
        onCancel={() => closeModal(setItineraryModalOpen)}
        footer={
          <Button href={fileUrlData} target="_blank" type="primary">
            Download
          </Button>
        }
      >
        <div id="itinerary-file">
          {fileUrlData === "" ? (
            <Spin />
          ) : (
            <Document
              file={fileUrlData}
              onLoadSuccess={onDocumentLoadSuccess}
              error={"There is no itinerary available."}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <Page key={`page_${index + 1}`} pageNumber={index + 1} />
              ))}
            </Document>
          )}
        </div>
      </Modal>

      <Modal
        title={
          <Flex vertical>
            <div className="custom-modal-header-title">Departure Date(s)</div>
            <div className="custom-modal-header-legends">
              <Legends />
            </div>
          </Flex>
        }
        footer={
          <Button
            disabled={selectedDepartureDate === ""}
            href={itineraryLinkByDate(itineraryData, selectedDepartureDate)}
          >
            Confirm
          </Button>
        }
        open={dateModalOpen}
        onCancel={() => closeModal(setDateModalOpen)}
        width={940}
        centered
        className="tour_details_all_dates"
      >
        <div className="date-selector">
          <Space size={8} wrap>
            {departureDatesData &&
              departureDatesData.map((ddate: { type: string; date: string }, index) =>
                departureDate(ddate.type, ddate.date),
              )}
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default ListingsV1;
